// src/pages/shop/ShopWholesaleSale.jsx
//
// Экран создания накладной оптовой отгрузки.
// Похож на кассу, но проще:
//   - выбор оптового клиента сверху
//   - добавление товаров (поиск + кнопка +)
//   - редактирование цены и количества каждой позиции
//   - комментарий
//   - кнопка "Отгрузить" → создаёт WholesaleSale и списывает остатки

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  counterparties as cpApi,
  wholesaleSales as wsApi,
  products as productsApi,
  categories as categoriesApi,
} from '../../api';
import { useToast } from '../../hooks/useToast';
import { useMyCurrency, useMyShop } from '../../hooks/useMyShop';
import { formatMoney } from '../../data/currencies';
import Icon from '../../components/Icon';
import SafeInput from '../../components/SafeInput';
import VariantPickerSheet from '../../components/VariantPickerSheet';
import ScanButton from '../../components/ScanButton';
import ShopWholesaleSaleDm from './ShopWholesaleSaleDm';

export default function ShopWholesaleSale() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetClientId = searchParams.get('client') || null;
  const { show, ToastView } = useToast();
  const currency = useMyCurrency();
  const shop = useMyShop();

  // Состояние
  const [clients, setClients]       = useState([]);
  const [clientId, setClientId]     = useState(presetClientId || '');
  const [allProducts, setAllProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCatId, setSelectedCatId] = useState('all'); // 'all' | id
  const [productSearch, setProductSearch] = useState('');
  const [cart, setCart]             = useState([]);
  const [note, setNote]             = useState('');
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cps, ps, cats] = await Promise.all([
          cpApi.list({ type: 'wholesale' }),
          productsApi.list().catch(() => []),     // DM-режиму товары не нужны; нет прав — пустой список
          categoriesApi.list().catch(() => []),  // если нет прав — пустой список
        ]);
        setClients(cps.items || []);
        setAllProducts(Array.isArray(ps) ? ps : (ps.items || []));
        setAllCategories(Array.isArray(cats) ? cats : (cats.items || []));
      } catch (e) {
        show(e.message || t('common.error'), 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const client = clients.find(c => c.id === clientId) || null;

  // Фильтрация товаров: и по поиску, и по категории
  const filteredProducts = useMemo(() => {
    let list = allProducts;
    // Категория
    if (selectedCatId && selectedCatId !== 'all') {
      list = list.filter(p => p.categoryId === selectedCatId);
    }
    // Поиск
    const q = productSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.article && String(p.article).toLowerCase().includes(q))
      );
    }
    return list;
  }, [allProducts, productSearch, selectedCatId]);

  const [variantPickerProduct, setVariantPickerProduct] = useState(null);

  // Фактически выполняет добавление в корзину (после возможного выбора варианта)
  const addItemToCart = (product, variant) => {
    const variantId = variant?.id || null;
    const variantName = variant?.name
      || (variant?.attributeValues
          ? Object.values(variant.attributeValues).filter(Boolean).join(' / ')
          : null);
    const salePrice = variant?.salePrice ?? product.salePrice ?? 0;
    const costPrice = variant?.costPrice ?? product.costPrice ?? 0;
    const stockLeft = variant?.stock ?? product.stock ?? 0;
    const unit = product.unit || 'шт';

    // Если уже в корзине — увеличим кол-во
    const idx = cart.findIndex(i => i.productId === product.id && i.variantId === variantId);
    if (idx !== -1) {
      const next = [...cart];
      next[idx] = { ...next[idx], qty: Number(next[idx].qty) + 1 };
      setCart(next);
    } else {
      setCart([...cart, {
        key: `${product.id}-${variantId || 'def'}-${Date.now()}`,
        productId: product.id,
        variantId,
        variantName,
        name: product.name + (variantName ? ` (${variantName})` : ''),
        qty: 1,
        unit,
        salePrice,
        costPrice,
        stockLeft,
      }]);
    }
    // Поиск НЕ сбрасываем — пользователь может добавить ещё товары
  };

  // Клик по найденному в поиске товару
  const addToCart = (product) => {
    const variants = (product.variants || []).filter(v => v.active !== false);
    // Если есть несколько активных вариантов с атрибутами — спросим какой
    if (variants.length > 1) {
      setVariantPickerProduct(product);
      return;
    }
    // Если один вариант — берём его
    if (variants.length === 1) {
      addItemToCart(product, variants[0]);
      return;
    }
    // Если вариантов нет — добавляем как простой товар
    addItemToCart(product, null);
  };

  // Юзер выбрал вариант в VariantPickerSheet
  const onPickVariant = (variant) => {
    const product = variantPickerProduct;
    setVariantPickerProduct(null);
    if (product) addItemToCart(product, variant);
  };

  // Сканирование штрихкода — найти товар (с вариантом) и добавить в накладную.
  const handleScan = async (code) => {
    try {
      const r = await productsApi.byBarcode(code);
      if (r?.product) {
        // Если найден вариант — добавляем сразу
        if (r.variant) {
          addItemToCart(r.product, r.variant);
          return;
        }
        // Если только продукт без варианта — используем общую логику addToCart
        // (она откроет picker если есть варианты)
        addToCart(r.product);
        return;
      }
    } catch (e) {
      // Не найден — упадём ниже
    }
    show(t('products.barcodeNotFound', 'Товар с таким баркодом не найден'), 'error');
  };

  const updateItem = (key, patch) => {
    setCart(cart.map(i => i.key === key ? { ...i, ...patch } : i));
  };

  const removeItem = (key) => {
    setCart(cart.filter(i => i.key !== key));
  };

  const total = cart.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.salePrice) || 0), 0);

  const submit = async () => {
    if (!clientId) {
      show(t('wholesale.errSelectClient'), 'error');
      return;
    }
    if (cart.length === 0) {
      show(t('wholesale.errEmptyCart'), 'error');
      return;
    }
    for (const item of cart) {
      const qty = Number(item.qty);
      if (!isFinite(qty) || qty <= 0) {
        show(t('wholesale.errInvalidQty', { name: item.name }), 'error');
        return;
      }
    }

    if (!window.confirm(t('wholesale.confirmShip', { client: client?.name || '', total: formatMoney(total, currency) }))) {
      return;
    }

    setSaving(true);
    try {
      const data = {
        counterpartyId: clientId,
        items: cart.map(i => ({
          productId: i.productId,
          variantId: i.variantId,
          name: i.name,
          qty: Number(i.qty),
          unit: i.unit,
          salePrice: Number(i.salePrice),
          costPrice: Number(i.costPrice) || 0,
        })),
        note: note.trim() || undefined,
      };
      const sale = await wsApi.create(data);
      show(t('wholesale.shipped'));
      // Переходим в карточку контрагента — там увидим эту накладную
      navigate(`/counterparty/${sale.counterparty.id}`);
    } catch (e) {
      show(e.message || t('common.error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loader" style={{ padding: 64 }}><div className="spinner" /></div>
      </div>
    );
  }

  // Режим отгрузки по DataMatrix — отдельный экран сбора
  if (shop?.wholesaleByDataMatrix) {
    return <ShopWholesaleSaleDm clients={clients} presetClientId={clientId} />;
  }

  return (
    <div className="page" style={{ paddingBottom: 32 }}>
      {ToastView}

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 className="page-title">{t('wholesale.newShipment')}</h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Выбор клиента */}
        <div className="input-group">
          <label>{t('wholesale.clientLabel')}</label>
          {clients.length === 0 ? (
            <div style={{
              padding: 12, background: 'var(--bg-subtle)',
              borderRadius: 'var(--r-sm)', fontSize: 13, color: 'var(--fg-muted)',
            }}>
              {t('wholesale.noClients')}
              <button
                onClick={() => navigate('/counterparties')}
                className="btn btn-secondary btn-sm"
                style={{ marginTop: 8, width: '100%' }}
              >
                {t('wholesale.goToCounterparties')}
              </button>
            </div>
          ) : (
            <select
              className="select"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">— {t('common.notSet')} —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Поиск товаров — обычный input для моментальной реакции */}
        <div className="input-group">
          <label>{t('wholesale.addProduct')}</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              className="input"
              style={{ flex: 1 }}
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder={t('wholesale.searchProduct')}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <ScanButton onScan={handleScan} size="lg" />
          </div>
        </div>

        {/* Горизонтальный список категорий-чипов */}
        {allCategories.length > 0 && (
          <div style={{
            display: 'flex', gap: 6, overflowX: 'auto',
            paddingBottom: 8, marginBottom: 12,
            scrollbarWidth: 'none',
          }}>
            <button
              onClick={() => setSelectedCatId('all')}
              style={chipStyle(selectedCatId === 'all')}
            >
              {t('wholesale.allCategories')}
            </button>
            {allCategories.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedCatId(c.id)}
                style={chipStyle(selectedCatId === c.id)}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}

        {/* Список товаров — виден всегда (фильтруется по категории + поиску) */}
        {filteredProducts.length > 0 ? (
          <div style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            marginBottom: 12, maxHeight: 320, overflow: 'auto',
          }}>
            {filteredProducts.slice(0, 50).map(p => {
              const variants = (p.variants || []).filter(v => v.active !== false);
              const hasMultipleVariants = variants.length > 1;
              // Считаем сколько этого товара (всех его вариантов) уже в корзине
              const inCartQty = cart
                .filter(i => i.productId === p.id)
                .reduce((s, i) => s + (Number(i.qty) || 0), 0);
              return (
                <div
                  key={p.id}
                  onClick={() => addToCart(p)}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: inCartQty > 0 ? 'var(--brand-primary-soft)' : 'transparent',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                      {hasMultipleVariants && (
                        <span style={{
                          marginLeft: 6, fontSize: 10, fontWeight: 700,
                          color: 'var(--brand-accent)',
                          background: 'var(--brand-accent-soft)',
                          padding: '2px 6px', borderRadius: 4,
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                        }}>
                          {variants.length} {t('wholesale.variantsCount')}
                        </span>
                      )}
                    </p>
                    <p style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                      {formatMoney(p.salePrice || 0, currency)} · {t('stockOp.stockLeft', { qty: p.stock || 0, unit: p.unit || 'шт' })}
                    </p>
                  </div>
                  {inCartQty > 0 ? (
                    <div style={{
                      minWidth: 28, height: 28, borderRadius: 14,
                      background: 'var(--brand-primary)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 800,
                      padding: '0 8px',
                      fontVariantNumeric: 'tabular-nums',
                    }}>{inCartQty}</div>
                  ) : (
                    <Icon name={hasMultipleVariants ? 'chevronRight' : 'plus'} size={16} color="var(--brand-primary)" />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{
            padding: 24, marginBottom: 12,
            background: 'var(--bg-subtle)',
            borderRadius: 'var(--r-sm)',
            textAlign: 'center', fontSize: 13, color: 'var(--fg-muted)',
          }}>
            {productSearch ? t('wholesale.noProductsFound') : t('wholesale.noProductsInCategory')}
          </div>
        )}

        {/* Корзина */}
        {cart.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '12px 0 8px' }}>
              {t('wholesale.cartTitle')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cart.map(item => (
                <div
                  key={item.key}
                  style={{
                    position: 'relative',
                    padding: 12,
                    paddingRight: 44,
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)',
                  }}
                >
                  <button
                    onClick={() => removeItem(item.key)}
                    style={{
                      position: 'absolute',
                      top: 8, right: 8,
                      width: 32, height: 32,
                      padding: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--danger)',
                      cursor: 'pointer',
                      borderRadius: 'var(--r-xs)',
                    }}
                  >
                    <Icon name="trash" size={16} />
                  </button>

                  <p style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, marginBottom: 4 }}>
                    {item.name || '—'}
                  </p>
                  <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginBottom: 8 }}>
                    {t('stockOp.stockLeft', { qty: item.stockLeft, unit: item.unit })}
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
                    <div>
                      <label style={miniLabel}>{t('wholesale.qty')}</label>
                      <SafeInput
                        type="number"
                        inputMode="decimal"
                        value={item.qty}
                        onChange={(e) => updateItem(item.key, { qty: e.target.value })}
                      />
                    </div>
                    <div>
                      <label style={miniLabel}>{t('wholesale.price')}</label>
                      <SafeInput
                        type="number"
                        inputMode="decimal"
                        value={item.salePrice}
                        onChange={(e) => updateItem(item.key, { salePrice: e.target.value })}
                      />
                    </div>
                    <div>
                      <label style={miniLabel}>{t('wholesale.sum')}</label>
                      <div style={{
                        padding: '8px 10px',
                        background: 'var(--bg-subtle)',
                        borderRadius: 'var(--r-xs)',
                        fontSize: 14, fontWeight: 700,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {formatMoney((Number(item.qty) || 0) * (Number(item.salePrice) || 0), currency)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Заметка */}
        {cart.length > 0 && (
          <div className="input-group" style={{ marginTop: 12 }}>
            <label>{t('wholesale.note')}</label>
            <SafeInput
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('wholesale.notePh')}
            />
          </div>
        )}

        {/* Итого + кнопка */}
        {cart.length > 0 && (
          <>
            <div style={{
              padding: 16, marginTop: 12,
              background: 'var(--brand-primary-soft)',
              borderRadius: 'var(--r-md)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-primary)' }}>
                {t('wholesale.total')}
              </p>
              <p style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(total, currency)}
              </p>
            </div>

            <button
              onClick={submit}
              disabled={saving || !clientId}
              className="btn btn-primary btn-lg"
              style={{ width: '100%', marginTop: 12 }}
            >
              {saving ? t('common.saving') : t('wholesale.shipBtn')}
            </button>
          </>
        )}

        <div style={{ height: 40 }} />
      </div>

      {variantPickerProduct && (
        <VariantPickerSheet
          product={variantPickerProduct}
          currency={currency}
          onPick={onPickVariant}
          onClose={() => setVariantPickerProduct(null)}
        />
      )}
    </div>
  );
}

const miniLabel = {
  fontSize: 10, fontWeight: 700,
  color: 'var(--fg-muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  display: 'block', marginBottom: 4,
};

const chipStyle = (active) => ({
  padding: '6px 14px',
  borderRadius: 999,
  border: '1px solid',
  borderColor: active ? 'var(--brand-primary)' : 'var(--border)',
  background: active ? 'var(--brand-primary-soft)' : 'var(--bg-elevated)',
  color: active ? 'var(--brand-primary)' : 'var(--fg-muted)',
  fontSize: 13, fontWeight: 600,
  whiteSpace: 'nowrap',
  cursor: 'pointer', flexShrink: 0,
});
